"use strict";

import { Box, Container, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import PaletteIcon from "@mui/icons-material/Palette";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Loader from "../Components/Loader.js";
import VoiceCreationSection from "../Components/VoiceCreation/VoiceCreationSection.js";
import { getProjectById } from "../features/projects/api.js";
import {
  GET_CHARACTERS_FOR_PROJECT,
  REMIX_CHARACTER_VOICE,
  DESIGN_CHARACTER_VOICE,
} from "../ApiEndpoints.js";

const CONTENT_MAX_WIDTH = 1120;

const VOICE_CREATION_MODES = {
  remix: {
    value: "remix",
    label: "Voice Remix",
    shortLabel: "Remix",
    description:
      "Use one or more existing character voices as references and transform them with a prompt.",
    endpoint: REMIX_CHARACTER_VOICE,
    allowMultipleCharacters: true,
    defaultVoiceDescription: "Make this voice have a Mexican Accent",
    icon: GraphicEqIcon,
    enabled: true,
  },
  design: {
    value: "design",
    label: "Voice Design",
    shortLabel: "Design",
    description:
      "Design a new voice variation for one selected character using only the prompt.",
    endpoint: DESIGN_CHARACTER_VOICE,
    allowMultipleCharacters: false,
    defaultVoiceDescription: "Design a warm, calm, natural character voice",
    icon: PaletteIcon,
    enabled: true,
  },
  match: {
    value: "match",
    label: "Voice Match",
    shortLabel: "Match",
    description:
      "Match or compare voices against character references. This section will be added later.",
    endpoint: null,
    allowMultipleCharacters: false,
    defaultVoiceDescription: "",
    icon: CompareArrowsIcon,
    enabled: false,
  },
};

const ModeCard = ({ mode, isActive, onClick }) => {
  const Icon = mode.icon;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          onClick();
        }
      }}
      sx={{
        cursor: "pointer",
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: "18px",
        border: isActive ? "1px solid #1f6f96" : "1px solid #dfe6ee",
        background: isActive
          ? "linear-gradient(135deg, rgba(31,111,150,0.14), rgba(255,255,255,0.96))"
          : "#ffffff",
        boxShadow: isActive
          ? "0 12px 28px rgba(31, 111, 150, 0.16)"
          : "0 4px 12px rgba(15, 23, 42, 0.05)",
        transition: "all 160ms ease",
        outline: "none",
        opacity: mode.enabled === false ? 0.86 : 1,
        "&:hover": {
          borderColor: "#1f6f96",
          transform: "translateY(-1px)",
          boxShadow: "0 12px 28px rgba(15, 23, 42, 0.1)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "13px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isActive ? "white" : "#1f6f96",
            backgroundColor: isActive ? "#1f6f96" : "rgba(31,111,150,0.09)",
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 850,
              color: isActive ? "#0f2f46" : "#1f2937",
              lineHeight: 1.2,
            }}
          >
            {mode.label}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#667085",
              mt: 0.5,
              lineHeight: 1.45,
            }}
          >
            {mode.description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const EmptyVoiceMatchSection = () => {
  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        border: "1px solid rgba(223, 230, 238, 0.95)",
        borderRadius: "26px",
        backgroundColor: "rgba(255,255,255,0.96)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid #edf0f5",
          background:
            "linear-gradient(135deg, rgba(31,111,150,0.08), rgba(255,255,255,0.96))",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 850,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          Voice Match
        </Typography>
      </Box>

      <Box
        sx={{
          p: { xs: 3, md: 4 },
          minHeight: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "#667085",
        }}
      >
        <Box>
          <CompareArrowsIcon sx={{ fontSize: 40, color: "#1f6f96", mb: 1 }} />

          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1f2937" }}>
            Voice Match
          </Typography>

          <Typography variant="body2" sx={{ mt: 0.5 }}>
            This section is empty for now.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const VoiceMixingScreen = () => {
  const params = useParams();
  const { id: projectId } = params;

  const [upLoader, setUpLoader] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [characters, setCharacters] = useState([]);
  const [activeMode, setActiveMode] = useState(VOICE_CREATION_MODES.remix.value);

  useEffect(() => {
    const fetchScreenData = async () => {
      try {
        setUpLoader(true);

        const [projectResponse, charactersResponse] = await Promise.all([
          getProjectById(projectId),
          axios.post(GET_CHARACTERS_FOR_PROJECT, {
            projectId,
          }),
        ]);

        const projectData = {
          ...projectResponse.data,
          id: projectResponse.data.id || projectId,
        };

        setProjectName(projectData.name || "");

        const loadedCharacters = Array.isArray(
          charactersResponse.data?.characters
        )
          ? charactersResponse.data.characters
          : [];

        const activeCharacters = loadedCharacters.filter(
          (character) => character.isUsed !== false
        );

        setCharacters(activeCharacters);
      } catch (error) {
        console.error("Failed to load voice generation data:", error);
        toast.error("Failed to load project characters.");
      } finally {
        setUpLoader(false);
      }
    };

    fetchScreenData();
  }, [projectId]);

  const activeModeConfig = useMemo(() => {
    return VOICE_CREATION_MODES[activeMode] || VOICE_CREATION_MODES.remix;
  }, [activeMode]);

  return (
    <Container
      maxWidth={false}
      style={{ fontFamily: "var(--font-body)" }}
      sx={{ py: { xs: 3, md: 5 } }}
    >
      <Loader Load={upLoader} />
      <ToastContainer position="top-center" />

      <Box
        sx={{
          width: "100%",
          maxWidth: CONTENT_MAX_WIDTH,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 3,
          px: { xs: 1.5, sm: 2 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            pt: 0.5,
            pb: 0.5,
          }}
        >
          <Box
            sx={{
              width: 46,
              height: 46,
              border: "1px solid rgba(255,255,255,0.7)",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 22px rgba(15, 23, 42, 0.1)",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(245,249,252,0.92))",
              color: "#1f6f96",
            }}
          >
            <AutoAwesomeIcon fontSize="small" />
          </Box>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: 30, md: 40 },
              letterSpacing: "-0.04em",
              color: "#050b14",
              lineHeight: 1.1,
            }}
          >
            Voice Mixing
          </Typography>
        </Box>

        <Box
          sx={{
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid rgba(223, 230, 238, 0.9)",
            borderRadius: "24px",
            backgroundColor: "rgba(255,255,255,0.94)",
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
            p: { xs: 2, md: 2.5 },
            backdropFilter: "blur(8px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 850,
                  color: "#101828",
                }}
              >
                Choose creation mode
              </Typography>
            </Box>

            <Box
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: "999px",
                backgroundColor: "rgba(31,111,150,0.08)",
                color: "#1f6f96",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              Active: {activeModeConfig.shortLabel}
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            <ModeCard
              mode={VOICE_CREATION_MODES.remix}
              isActive={activeMode === VOICE_CREATION_MODES.remix.value}
              onClick={() => setActiveMode(VOICE_CREATION_MODES.remix.value)}
            />

            <ModeCard
              mode={VOICE_CREATION_MODES.design}
              isActive={activeMode === VOICE_CREATION_MODES.design.value}
              onClick={() => setActiveMode(VOICE_CREATION_MODES.design.value)}
            />

            <ModeCard
              mode={VOICE_CREATION_MODES.match}
              isActive={activeMode === VOICE_CREATION_MODES.match.value}
              onClick={() => setActiveMode(VOICE_CREATION_MODES.match.value)}
            />
          </Box>
        </Box>

        {activeModeConfig.value === VOICE_CREATION_MODES.match.value ? (
          <EmptyVoiceMatchSection />
        ) : (
          <VoiceCreationSection
            key={activeModeConfig.value}
            title={activeModeConfig.label}
            description={activeModeConfig.description}
            endpoint={activeModeConfig.endpoint}
            projectId={projectId}
            projectName={projectName}
            characters={characters}
            allowMultipleCharacters={activeModeConfig.allowMultipleCharacters}
            defaultVoiceDescription={activeModeConfig.defaultVoiceDescription}
          />
        )}
      </Box>
    </Container>
  );
};

export default VoiceMixingScreen;
